import { logger, sanitizeError } from '@heidi/runtime/logger';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  GET_PATIENT_PROFILES,
  type PatientProfile,
  UNLINK_SESSIONS_TO_PATIENT_PROFILE,
} from '../../contracts';
import { unlinkSessionsToPatientProfile } from '../api/patients';
import { invalidatePatientRecap } from '../query/useQueryPatientRecap';

export type UnlinkOwnSessionsToPatientProfileArgs = {
  sessionIds: string[];
  patientProfile: PatientProfile;
  /** Passed as a query param for session direct-share permission check.
   *  When a session is directly shared, the sharee can process (create/update/link/unlink)
   *  patient profiles on that session by providing its ID here. */
  sessionId?: string;
};

/**
 * The FIRST-PERSON half of `useMutateUnlinkSessionsToPatientProfile`
 * (D2.2, ARCFE-1053).
 *
 * `DELETE /patient-profiles/{id}/sessions` is patients'; `DELETE /teams/
 * {teamId}/members/{memberId}/patient-profiles/{id}/sessions` is workspace's
 * and stayed in the app hub with the review-mode branch.
 *
 * Same split as the link hook: the patient-profiles invalidation (server-counted
 * `session_count` drops on unlink) and the recap invalidation (unlinking can
 * change the recap's source session) are patients caches and live here; the
 * linked-sessions-by-profile invalidation, the sessions-list invalidation and
 * the per-session `GET_SESSION_BY_ID` clear are Session's and stay app-side
 * behind `onUnlinked`, in their original relative order.
 *
 * The optimistic clear matters and must not be simplified when it is
 * sequenced app-side: it mirrors exactly what `unlink_patient_from_sessions`
 * does on the backend (nulls `patient_profile`, `extracted_patient_context`
 * and the embedded `patient.{name, demographic_details}` subdoc). The app
 * delegate owns that updater.
 */
export function useMutateUnlinkOwnSessionsToPatientProfile({
  isViewingOtherSession = false,
  onUnlinked,
  onUnlinkFailed,
}: {
  /** `isViewingOtherSessionAtom`, app-resolved (cohort-C). */
  isViewingOtherSession?: boolean;
  onUnlinked?: (input: UnlinkOwnSessionsToPatientProfileArgs) => void;
  onUnlinkFailed?: (error: unknown) => void;
} = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [UNLINK_SESSIONS_TO_PATIENT_PROFILE],
    mutationFn: async (input: UnlinkOwnSessionsToPatientProfileArgs) => {
      if (input.patientProfile.id) {
        const data = await unlinkSessionsToPatientProfile(
          input.patientProfile.id,
          input.sessionIds,
          isViewingOtherSession ? input.sessionId : undefined
        );
        return data;
      }
    },
    onSuccess: (_, input) => {
      logger.info('[PatientLink] Session unlinked from patient successfully');
      // session_count on the patient list / drawer is server-counted, so an
      // unlink has to invalidate the patient profiles cache or the FE keeps
      // showing the stale pre-unlink count.
      void queryClient.invalidateQueries({
        queryKey: [GET_PATIENT_PROFILES],
      });
      // Unlinking can change the recap's source session. The recap caches
      // indefinitely, so refetch it or the popover + drawer keep showing the
      // pre-unlink summary.
      invalidatePatientRecap(queryClient, input.patientProfile?.id);
      onUnlinked?.(input);
    },
    onError: (error) => {
      logger.error(
        '[PatientLink] Failed to unlink session from patient',
        sanitizeError(error)
      );
      onUnlinkFailed?.(error);
    },
  });
}
